import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, Observable, Subscription, switchMap, take } from 'rxjs';
import { addDays, max, startOfDay } from 'date-fns';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CibPlaceVoteComponent } from '../../cib-place-vote/cib-place-vote.component';
import { ActivatedRoute } from '@angular/router';
import { materialSymbols } from '../../../../../shared/constants/material-symbols';
import { UnifiedBallotStatus } from '../../../../../shared/enums/unified-ballot-status-enum';
import { IApiResponse } from '../../../../../shared/models/api-response';
import { IBlobFile } from '../../../../../shared/models/blob-file';
import { IUser } from '../../../../../shared/models/user';
import { AuthService } from '../../../../../shared/services/auth.service';
import { FileDownloadUtility } from '../../../../../shared/utilities/blob-download-utility';
import { UserUtility } from '../../../../../shared/utilities/user-utility';
import { DateFormatPipe } from '../../../../controls/pipes/date-format.pipe';
import { SummaryComponentView } from '../../../shared/components/button-vote-now/button-vote-now.component';
import { IBallotCommitteeMemberDto } from '../../../shared/models/ballot-base/ballot-committee-member-dto';
import { IBallotVoteDto } from '../../../shared/models/ballot-base/ballot-vote-dto';
import { IUpdateBallotDatesRequest } from '../../../shared/models/ballot-base/update-ballot-dates-request';
import { IUpdateBallotNoteRequest } from '../../../shared/models/ballot-base/update-ballot-note-request';
import { ciVoteOption } from '../../../shared/models/ci/ci-vote';
import { ICommitteeInternalBallotDto } from '../../../shared/models/ci/committee-internal-ballot-dto';
import { CiBallotService } from '../../../shared/services/ci-ballot.service';

@Component({
    selector: 'app-ci-summary',
    templateUrl: './ci-summary.component.html',
    styleUrl: './ci-summary.component.scss',
    standalone: false
})
export class CiSummaryComponent implements OnInit, OnDestroy, OnChanges {
  @Input() ciBallotDetailDto: ICommitteeInternalBallotDto | null = null;
  @Output() onPlaceVote = new EventEmitter<string>();

  subscriptions: Subscription = new Subscription();
  isUserPmOrAbove: boolean = false;
  canEditStartDate: boolean = false;
  canEditEndDate: boolean = false;
  minStartDate: Date | null = null;
  maxStartDate: Date | null = null;
  minEndDate: Date | null = null;
  currentUser: IUser | null = null;

  ballotCommitteeMember: IBallotCommitteeMemberDto | undefined ;
  userVote: IBallotVoteDto | undefined;
  ballotVotes: IBallotVoteDto[] = [];
  isJoint: boolean = false;
  isVoteButtonEnable: boolean = false;
  hasVoted: boolean = false;
  isVotingActionsVisible: boolean = false;
  isInternalPublicUser: boolean = false;

  agree = materialSymbols.thumbUp;
  disagree = materialSymbols.thumbDown;

  userVoteOption: any;
  userVoteSubmittedDate: string | undefined;

  ballotNoteFormGroup = new FormGroup({
    ballotNoteCtrl: new FormControl<string | null>(null),
  });

  ballotDateFormGroup = new FormGroup({
    ballotStartDateCtrl: new FormControl<Date | null>(null),
    ballotEndDateCtrl: new FormControl<Date | null>(null),
  });

  get ballotNoteCtrl() {
    return this.ballotNoteFormGroup.get('ballotNoteCtrl');
  }

  get ballotStartDateCtrl() {
    return this.ballotDateFormGroup.get('ballotStartDateCtrl');
  }

  get ballotEndDateCtrl() {
    return this.ballotDateFormGroup.get('ballotEndDateCtrl');
  }

  openVotePopup: boolean = false;

  constructor(
    private _authService: AuthService,
    private _ciBallotService: CiBallotService,
    private _modalService: NgbModal,
    private _router: ActivatedRoute,
    private dateFormatPipe: DateFormatPipe
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.setupCurrentUserSubscription();
    this.setupUpdateBallotNoteSubscription();
    this.setupBallotDateSubscription();

    this.setViewRedirection();

    this.initVoteButton();

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['ciBallotDetailDto'] &&
      changes['ciBallotDetailDto'].currentValue
    ) {
      this.loadBallotNote(
        changes['ciBallotDetailDto'].currentValue.ballotSummary.notes
      );
      this.loadBallotDates(
        changes['ciBallotDetailDto'].currentValue.ballotSummary.startDate,
        changes['ciBallotDetailDto'].currentValue.ballotSummary.endDate
      );
      this.setBallotDatePermissions();
    }
  }

  setupCurrentUserSubscription(): void {
    this.subscriptions.add(this._authService.currentUserSubject
      .pipe(
        filter(user => !!user),
        take(1)
      )
      .subscribe({
      next: (user) => {
        this.currentUser = user;
        this.isUserPmOrAbove = user ? UserUtility.userIsPmOrAbove(user) : false;
        this.isInternalPublicUser = UserUtility.isInternalPublicUser(this.currentUser);
        this.setBallotDatePermissions();
      }
    }));
  }

  setupUpdateBallotNoteSubscription(): void {
    this.subscriptions.add(
      this.ballotNoteFormGroup.valueChanges
        .pipe(
          debounceTime(1000),
          distinctUntilChanged(
            (prev, curr) => prev.ballotNoteCtrl === curr.ballotNoteCtrl
          ),
          switchMap((formData: Partial<INotesFormData>) =>
            this.saveBallotNote(formData)
          )
        )
        .subscribe({
          next: (apiResponse) => {
            if (!apiResponse || !apiResponse.success) {
              console.log(
                `Failed to save Ci ballot note. Error: ${apiResponse?.message}`
              );
            }
          },
          error: (error) => {
            console.log(`Error saving CI ballot notes: ${error}`);
          },
        })
    );
  }

  setupBallotDateSubscription(): void {
    this.subscriptions.add(
      this.ballotDateFormGroup.valueChanges
        .pipe(
          debounceTime(800),
          distinctUntilChanged(
            (prev, curr) =>
              prev.ballotStartDateCtrl === curr.ballotStartDateCtrl &&
              prev.ballotEndDateCtrl === curr.ballotEndDateCtrl
          ),
          switchMap((formData: Partial<IBallotDateFormData>) =>
            this.updateBallotDates(formData)
          )
        )
        .subscribe({
          next: (apiResponse) => {
            if (!apiResponse || !apiResponse.success) {
              console.log(
                `Failed to save CI ballot dates. Error: ${apiResponse?.message}`
              );
            }

            const startDate = this.ballotStartDateCtrl?.value || new Date();
            const endDate = this.ballotEndDateCtrl?.value || new Date();
            this.loadBallotDates(startDate, endDate);
          },
          error: (error) => {
            console.log(`Error saving CI ballot dates: ${error}`);
          },
        })
    );
  }

  loadBallotNote(ballotNote: string | null): void {
    this.ballotNoteFormGroup.patchValue(
      {
        ballotNoteCtrl: ballotNote,
      },
      { emitEvent: false } // Don't trigger the form's valueChanges event
    );
  }

  saveBallotNote(
    formValue: Partial<INotesFormData>
  ): Observable<IApiResponse<string>> {
    var request: IUpdateBallotNoteRequest = {
      ballotId: this.ciBallotDetailDto?.id || '',
      notes:
        !formValue.ballotNoteCtrl || formValue.ballotNoteCtrl === ''
          ? null
          : formValue.ballotNoteCtrl,
    };

    var obs$ = this._ciBallotService.updateBallotNote(request);
    return obs$;
  }

  loadBallotDates(startDate: Date, endDate: Date): void {
    startDate = startOfDay(startDate);
    endDate = startOfDay(endDate);

    this.ballotDateFormGroup.patchValue(
      {
        ballotStartDateCtrl: startDate,
        ballotEndDateCtrl: endDate,
      },
      { emitEvent: false } // Don't trigger the form's valueChanges event
    );

    const today = startOfDay(new Date());
    this.minStartDate = addDays(today, 1);
    this.maxStartDate = addDays(endDate, -1);

    this.minEndDate = max([addDays(today, 2), addDays(startDate, 1)]);
  }

  updateBallotDates(
    formValue: Partial<IBallotDateFormData>
  ): Observable<IApiResponse<string>> {
    let request: IUpdateBallotDatesRequest = {
      ballotId: this.ciBallotDetailDto?.id || '',
      startDate: formValue.ballotStartDateCtrl || null,
      endDate: formValue.ballotEndDateCtrl || null,
    };

    let obs$ = this._ciBallotService.updateBallotDates(request);
    return obs$;
  }

  getCanEditStartDate(): boolean {
    if (!this.ciBallotDetailDto?.ballotSummary?.unifiedBallotStatusId) {
      return false;
    }

    const ballotStatus =
      this.ciBallotDetailDto?.ballotSummary?.unifiedBallotStatusId;

    const canEditStartDate =
      ballotStatus === UnifiedBallotStatus.scheduled && this.isUserPmOrAbove;
    return canEditStartDate;
  }

  getCanEditEndDate(): boolean {
    if (!this.ciBallotDetailDto?.ballotSummary?.unifiedBallotStatusId) {
      return false;
    }

    const ballotStatus =
      this.ciBallotDetailDto?.ballotSummary?.unifiedBallotStatusId;

    const canEditEndDate =
      (ballotStatus === UnifiedBallotStatus.scheduled ||
        ballotStatus === UnifiedBallotStatus.openForVoting) &&
      this.isUserPmOrAbove;
    return canEditEndDate;
  }

  setBallotDatePermissions(): void {
    if (!this.isUserPmOrAbove) {
      this.ballotNoteCtrl?.disable();
    } else {
      this.ballotNoteCtrl?.enable();
    }
    this.canEditStartDate = this.getCanEditStartDate();
    this.canEditEndDate = this.getCanEditEndDate();
  }

  downloadAssociatedDocument(events: MouseEvent) {
    if (!this.ciBallotDetailDto?.supportingDocument) {
      return;
    }

    let blobOverview = this.ciBallotDetailDto.supportingDocument;

    this.subscriptions.add(
      this._ciBallotService
        .downloadSupportingDocument(this.ciBallotDetailDto.id)
        .subscribe({
          next: (response) => {
            if (!response || !response.success) {
              console.log(
                `Failed to download supporting document: ${response?.message}`
              );
            } else {
              let blobFile: IBlobFile = response.content;
              FileDownloadUtility.downloadBlobToBrowser(blobFile);
            }
          },
          error: (error) => {
            console.log(`Error downloading supporting document: ${error}`);
          },
        })
    );
  }

  initVoteButton() {
    this.ballotCommitteeMember = this.ciBallotDetailDto?.committeeMembers?.find(committee => committee.email == this.currentUser?.email);
    this.userVote = this.ballotCommitteeMember?.votes[0];
    this.isVoteButtonEnable = this.ciBallotDetailDto?.ballotSummary?.unifiedBallotStatusId == UnifiedBallotStatus.openForVoting;
    this.isVotingActionsVisible = this.ballotCommitteeMember?.isEligible??false;
    this.hasVoted = this.userVote != undefined || this.userVote != null;

    this.redirectToVoteIfRequired();

    if (!this.userVote) return;

    this.userVoteOption = ciVoteOption[this.userVote?.voteOptionType ?? ''];
    this.userVoteSubmittedDate = this.dateFormatPipe.transform(this.userVote?.lastUpdated);
  }

  setViewRedirection() {
    const voteView = this._router?.snapshot?.queryParamMap?.get(
      SummaryComponentView.vote
    );
    if (voteView) this.openVotePopup = true;
  }

  redirectToVoteIfRequired() {

    if (this.openVotePopup && !this.userVote) {
      this.openVotePopup = false;
      this.openPlaceVoteModal();
    }
  }

  refreshVoteSection(voteOption: string) {
    this.hasVoted = true;
    this.userVoteOption = ciVoteOption[voteOption];
    this.userVoteSubmittedDate = this.dateFormatPipe.transform(new Date());
  }

  openPlaceVoteModal() {
    const modalRef = this._modalService.open(CibPlaceVoteComponent,  { windowClass: 'place-vote-custom-modal', centered: true});
    modalRef.componentInstance.ballotCommitteeMember = this.ballotCommitteeMember;
    modalRef.componentInstance.user = this.currentUser;

    modalRef.result.then(
      (result) => {
        console.log('Modal closed with:', result);
      },
      (vote) => {
        if (vote) {
          this.refreshVoteSection(vote);
          this.onPlaceVote?.emit(this.ciBallotDetailDto?.id);
        }
      }
    );
  }
}

interface INotesFormData {
  ballotNoteCtrl: string | null;
}

interface IBallotDateFormData {
  ballotStartDateCtrl: Date | null;
  ballotEndDateCtrl: Date | null;
}


